import {
  createContext,
  useContext,
  useRef,
  useCallback,
  useId,
  useState,
  type RefObject,
  type ReactNode,
} from 'react';

export const dialogVariantOptions = ['compact', 'standard'] as const;
export type DialogVariant = (typeof dialogVariantOptions)[number];

export type DialogContextType = {
  close: () => void;
  dialogRef: RefObject<HTMLDialogElement | null>;
  id: string;
  isOpen: boolean;
  open: () => void;
  variant: DialogVariant;
};

export const DialogContext = createContext<DialogContextType | undefined>(
  undefined,
);

export type DialogRootProps = {
  children: ReactNode;
  onClose?: () => void;
  variant?: DialogVariant;
};

export const DialogRoot = (props: DialogRootProps) => {
  const { children, onClose, variant = 'standard' } = props;
  const id = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => {
    dialogRef.current?.showModal();
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    dialogRef.current?.close();
    setIsOpen(false);

    if (onClose) {
      onClose();
    }
  }, [onClose]);

  return (
    <DialogContext.Provider
      value={{ dialogRef, id, open, close, isOpen, variant }}
    >
      {children}
    </DialogContext.Provider>
  );
};

export const useDialog = (): DialogContextType => {
  const context = useContext(DialogContext);

  if (context === undefined) {
    throw new Error('useDialog must be used within a DialogRoot');
  }

  return context;
};
